/**
 * THE LINE BREAKER — one implementation, imported by both `ReflowOp` (here) and CONVERT's
 * `resegment()`, so a sentence breaks in the same place whether it arrived from a file or
 * from ASR and a user's clause-breaker edits apply to both.
 *
 * It is a pure function of (text, `LineBreakOptions`, `Measure`). No dependency on the reflow
 * op, no document, no context — `resegment` can call it on a sentence it has not yet turned
 * into a cue.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * WIDTH IS CELLS, NEVER CHARACTERS. `maxCells` is grapheme cells with full-width = 2, which
 * is why 42 (latin) and 32 (CJK) are the same quantity: Netflix's 16 Chinese characters and
 * 42 English characters both come to ~32–42 cells. Every width here goes through
 * `Measure.measure`; nothing calls `string.length`.
 *
 * ALGORITHM. For the two-line case the search space is one break point among a few dozen
 * candidates, so the "expensive" answer is free: the balanced path is an exact
 * minimum-raggedness DP over candidate positions, O(candidates² × lines), measured below the
 * noise floor at 40,000 cues. Knuth–Plass buys nothing here — its value is global
 * optimisation across a paragraph, and a subtitle cue is one to three lines with a hard cell
 * ceiling, so the global optimum and the per-cue optimum are the same thing.
 *
 * SHAPE. Netflix's line treatment prefers a BOTTOM-HEAVY pyramid (top line shorter) for
 * bottom-positioned subtitles and a top-heavy one for top-positioned subtitles; the BBC's
 * guidance is the opposite (longer line on top). Both are real conventions, so the shape is
 * a parameter with the Netflix default, flipped for top-aligned cues by `ReflowOp`.
 *
 * CLAUSE BREAKERS carry one convention, documented here because this implementation is what
 * both consumers import:
 *
 *   an entry that BEGINS WITH A SPACE means "prefer a break BEFORE this word"
 *   every other entry means                "prefer a break AFTER this string"
 *
 * Netflix's rule is literally "break after punctuation, before conjunctions, before
 * prepositions", and the second half is not expressible as a break-after string. Rank is the
 * entry's index: earlier outranks later.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

import type {
  CjkOptions,
  ClauseBreaker,
  LineBreakOptions,
  Measure,
} from '../../../contracts/cue-model.ts';
import { normalizeClauseBreaker } from '../../core/text/clause.ts';
import { isCjkCodePoint } from '../../core/text/width.ts';
import { LINE_SHAPE } from './defaults.ts';

export type LineShape = 'bottom-heavy' | 'top-heavy';

export interface BreakExtras {
  /**
   * Character offsets at which a break is FORBIDDEN. `ReflowOp` fills this with every offset
   * strictly inside a run that carries a time: reflow never splits a timed run.
   */
  readonly forbidden?: ReadonlySet<number>;
  /** Which line should be shorter when the cost is otherwise equal. */
  readonly shape?: LineShape;
}

export interface BrokenLine {
  /** Character offsets into the input, content only — surrounding whitespace is excluded. */
  readonly from: number;
  readonly to: number;
  readonly cells: number;
  readonly text: string;
}

export interface BreakResult {
  readonly lines: readonly BrokenLine[];
  /** Character offsets at which a new line begins (ascending, never includes 0). */
  readonly offsets: readonly number[];
  /** true when some line still exceeds `maxCells` — the caller decides the overflow policy. */
  readonly overflow: boolean;
  /** true when the text needed a break and the script offered nowhere legal to put one. */
  readonly noBreakOpportunity: boolean;
  /**
   * Set when `noBreakOpportunity` is true because the text is in a script with no inter-word
   * spaces and no punctuation classes to lean on — Thai, Khmer, Lao, Burmese. Correct
   * breaking there needs DICTIONARY segmentation, which this boundary cannot have (`Intl` is
   * banned inside it and a dictionary is megabytes). We decline to break mid-word rather than
   * break mid-word silently.
   */
  readonly needsDictionary: boolean;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Tuning weights. Not thresholds a user sets — the shape of the cost function.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Cost added for a break that lands on no clause boundary at all. */
const NO_CLAUSE_PENALTY = 24;
/** Cost added per rank step, so a comma break loses to a full-stop break. */
const CLAUSE_RANK_STEP = 0.75;
/** Cost added per cell by which a line exceeds `maxCells` — a hard preference, not a ban. */
const OVERFLOW_PENALTY = 1000;
/** Tiebreak weight for the pyramid shape. Small: it only decides otherwise-equal splits. */
const SHAPE_WEIGHT = 0.35;

/**
 * Southeast Asian scripts with no inter-word spaces. UAX #14 puts them in class SA
 * (Complex_Context) and says a dictionary is required; ICU carries one for exactly these.
 */
const NO_SPACE_SCRIPT_RE = /[฀-๿຀-໿ក-៿က-႟ꩠ-ꩿ]/u;

/*───────────────────────────────────────────────────────────────────────────────────────
  Cluster model
───────────────────────────────────────────────────────────────────────────────────────*/

interface Cluster {
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly cells: number;
  readonly space: boolean;
}

function clustersOf(text: string, measure: Measure): readonly Cluster[] {
  const bounds = measure.boundaries(text);
  const out: Cluster[] = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const start = bounds[i] as number;
    const end = bounds[i + 1] as number;
    if (end <= start) continue;
    const t = text.slice(start, end);
    out.push({ start, end, text: t, cells: measure.measure(t), space: /^\s+$/u.test(t) });
  }
  return out;
}

function firstCp(s: string): number {
  return s.codePointAt(0) ?? 0;
}
function lastCp(s: string): number {
  const cps = [...s];
  const last = cps[cps.length - 1];
  return last === undefined ? 0 : (last.codePointAt(0) ?? 0);
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Candidate break positions
───────────────────────────────────────────────────────────────────────────────────────*/

interface Candidate {
  /** Cluster index the new line starts at. */
  readonly at: number;
  /** Clause rank: 0 = best, -1 = no clause boundary. */
  readonly rank: number;
}

function kinsokuAllows(
  clusters: readonly Cluster[],
  at: number,
  cjk: CjkOptions,
  prevContent: number,
): boolean {
  if (!cjk.enabled) return true;
  const next = clusters[at];
  if (next !== undefined && cjk.noBreakBefore.includes(next.text[0] as string)) return false;
  const prev = clusters[prevContent];
  if (prev !== undefined) {
    const cps = [...prev.text];
    const lastChar = cps[cps.length - 1];
    if (lastChar !== undefined && cjk.noBreakAfter.includes(lastChar)) return false;
  }
  return true;
}

/**
 * Rank of the best clause breaker applicable at character offset `p`, or −1.
 *
 * Contract 1.4.0 (S10 C4) promoted the break-before convention CLEAN had encoded in the data
 * (a leading space) into the type as `ClauseBreaker`. The shorthand still works and this
 * function's behaviour is unchanged — the difference is that the leading-space rule is now
 * read in ONE place, `normalizeClauseBreaker` in src/core, instead of by the regex that used
 * to live inline here. An explicit `{ text, side: 'before' }` entry now works too.
 */
function clauseRank(
  text: string,
  contentEnd: number,
  p: number,
  breakers: readonly ClauseBreaker[],
): number {
  const before = text.slice(0, contentEnd);
  const lowerAfter = text.slice(p).toLowerCase();
  for (let r = 0; r < breakers.length; r++) {
    const entry = normalizeClauseBreaker(breakers[r] as ClauseBreaker);
    if (entry.text === '') continue;
    if (entry.side === 'before') {
      const word = entry.text.toLowerCase();
      if (!lowerAfter.startsWith(word)) continue;
      const after = lowerAfter.charAt(word.length);
      if (after !== '' && /[\p{L}\p{N}]/u.test(after)) continue; // must be a whole word
      return r;
    }
    if (before.endsWith(entry.text)) return r;
  }
  return -1;
}

function candidatesOf(
  text: string,
  clusters: readonly Cluster[],
  opts: LineBreakOptions,
  forbidden: ReadonlySet<number>,
): readonly Candidate[] {
  const out: Candidate[] = [];
  const seen = new Set<number>();
  for (let i = 1; i < clusters.length; i++) {
    const here = clusters[i] as Cluster;
    const prev = clusters[i - 1] as Cluster;
    if (here.space) continue; // a break belongs at the START of the next word, not in the gap

    // The last content cluster before this position, skipping the whitespace run.
    let pc = i - 1;
    while (pc >= 0 && (clusters[pc] as Cluster).space) pc--;
    if (pc < 0) continue; // leading whitespace only

    const afterSpace = prev.space;
    const cjkJoin =
      !afterSpace &&
      opts.cjk.enabled &&
      (isCjkCodePoint(firstCp(here.text)) ||
        isCjkCodePoint(lastCp((clusters[pc] as Cluster).text)));
    if (!afterSpace && !cjkJoin) continue;

    if (forbidden.has(here.start)) continue;
    if (!kinsokuAllows(clusters, i, opts.cjk, pc)) continue;
    if (seen.has(i)) continue;
    seen.add(i);

    const rank = opts.breakOnClause
      ? clauseRank(text, (clusters[pc] as Cluster).end, here.start, opts.clauseBreakers)
      : -1;
    out.push({ at: i, rank });
  }
  return out;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Line measurement over a cluster range
───────────────────────────────────────────────────────────────────────────────────────*/

interface Trimmed {
  readonly from: number;
  readonly to: number;
  readonly cells: number;
}

function trimRange(clusters: readonly Cluster[], a: number, b: number): Trimmed {
  let i = a;
  let j = b;
  while (i < j && (clusters[i] as Cluster).space) i++;
  while (j > i && (clusters[j - 1] as Cluster).space) j--;
  let cells = 0;
  for (let k = i; k < j; k++) cells += (clusters[k] as Cluster).cells;
  return { from: i, to: j, cells };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  breakOffsets — THE entry point
───────────────────────────────────────────────────────────────────────────────────────*/

export function breakOffsets(
  text: string,
  opts: LineBreakOptions,
  measure: Measure,
  extras: BreakExtras = {},
): BreakResult {
  const forbidden = extras.forbidden ?? new Set<number>();
  const shape = extras.shape ?? LINE_SHAPE;
  const clusters = clustersOf(text, measure);
  const n = clusters.length;

  const whole = trimRange(clusters, 0, n);
  const oneLine: BreakResult = {
    lines: [
      {
        from: charAt(clusters, whole.from, text.length),
        to: charEnd(clusters, whole.to),
        cells: whole.cells,
        text: sliceOf(clusters, whole.from, whole.to, text),
      },
    ],
    offsets: [],
    overflow: whole.cells > opts.maxCells,
    noBreakOpportunity: false,
    needsDictionary: false,
  };
  if (n === 0) {
    return {
      lines: [{ from: 0, to: 0, cells: 0, text: '' }],
      offsets: [],
      overflow: false,
      noBreakOpportunity: false,
      needsDictionary: false,
    };
  }
  if (whole.cells <= opts.maxCells || opts.maxLines <= 1) {
    if (whole.cells <= opts.maxCells) return oneLine;
    // maxLines is 1 and it does not fit: overflow, with nothing we are allowed to do.
    return { ...oneLine, overflow: true };
  }

  const candidates = candidatesOf(text, clusters, opts, forbidden);
  if (candidates.length === 0) {
    const needsDictionary = NO_SPACE_SCRIPT_RE.test(text);
    return { ...oneLine, overflow: true, noBreakOpportunity: true, needsDictionary };
  }

  // How few lines can this fit into? Netflix's rule is "one line unless it exceeds the
  // limit", so the target is the MINIMUM that fits, not `maxLines`.
  const minLines = greedyLineCount(clusters, candidates, opts.maxCells);
  const target = Math.min(Math.max(minLines, 2), opts.maxLines);

  const plan =
    opts.algorithm === 'greedy'
      ? greedyPlan(clusters, candidates, opts.maxCells, opts.maxLines)
      : balancedPlan(clusters, candidates, opts.maxCells, target, shape);

  const lines: BrokenLine[] = [];
  let overflow = false;
  for (const [a, b] of plan) {
    const t = trimRange(clusters, a, b);
    if (t.to <= t.from) continue;
    if (t.cells > opts.maxCells) overflow = true;
    lines.push({
      from: charAt(clusters, t.from, text.length),
      to: charEnd(clusters, t.to),
      cells: t.cells,
      text: sliceOf(clusters, t.from, t.to, text),
    });
  }
  if (lines.length === 0) return oneLine;

  return {
    lines,
    offsets: lines.slice(1).map((l) => l.from),
    overflow,
    noBreakOpportunity: false,
    needsDictionary: false,
  };
}

function charAt(clusters: readonly Cluster[], i: number, fallback: number): number {
  const c = clusters[i];
  return c === undefined ? fallback : c.start;
}
function charEnd(clusters: readonly Cluster[], j: number): number {
  const c = clusters[j - 1];
  return c === undefined ? 0 : c.end;
}
function sliceOf(clusters: readonly Cluster[], a: number, b: number, text: string): string {
  if (b <= a) return '';
  return text.slice((clusters[a] as Cluster).start, (clusters[b - 1] as Cluster).end);
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Greedy
───────────────────────────────────────────────────────────────────────────────────────*/

function greedyLineCount(
  clusters: readonly Cluster[],
  candidates: readonly Candidate[],
  maxCells: number,
): number {
  return greedyPlan(clusters, candidates, maxCells, Number.MAX_SAFE_INTEGER).length;
}

function greedyPlan(
  clusters: readonly Cluster[],
  candidates: readonly Candidate[],
  maxCells: number,
  maxLines: number,
): readonly (readonly [number, number])[] {
  const out: [number, number][] = [];
  const n = clusters.length;
  // `n` is a legal end point: without it the last line stops at the last CANDIDATE and the
  // tail becomes a line of its own, which inflates the line count and drags the balanced
  // path's target with it.
  const points = [...candidates.map((c) => c.at), n];
  let start = 0;
  while (start < n) {
    if (out.length + 1 >= maxLines) {
      out.push([start, n]);
      break;
    }
    let chosen = -1;
    for (const p of points) {
      if (p <= start) continue;
      if (trimRange(clusters, start, p).cells > maxCells) break;
      chosen = p;
    }
    if (chosen < 0) {
      // Nothing fits: take the first candidate past `start` so we make progress rather than
      // looping, and let the caller see the overflow.
      chosen = points.find((p) => p > start) ?? n;
    }
    out.push([start, chosen]);
    start = chosen;
    if (chosen >= n) break;
  }
  if (out.length === 0) out.push([0, n]);
  return out;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Balanced — exact minimum-raggedness DP over candidate positions
───────────────────────────────────────────────────────────────────────────────────────*/

function balancedPlan(
  clusters: readonly Cluster[],
  candidates: readonly Candidate[],
  maxCells: number,
  lines: number,
  shape: LineShape,
): readonly (readonly [number, number])[] {
  const n = clusters.length;
  const points = [0, ...candidates.map((c) => c.at), n];
  const rankAt = new Map<number, number>();
  for (const c of candidates) rankAt.set(c.at, c.rank);

  const total = trimRange(clusters, 0, n).cells;
  const targetCells = total / lines;

  const P = points.length;
  // dp[l][j] — cost of covering points[0..j] with `l` lines, ending exactly at points[j].
  const INF = Number.POSITIVE_INFINITY;
  const dp: number[][] = [];
  const back: number[][] = [];
  for (let l = 0; l <= lines; l++) {
    dp.push(new Array<number>(P).fill(INF));
    back.push(new Array<number>(P).fill(-1));
  }
  (dp[0] as number[])[0] = 0;

  for (let l = 1; l <= lines; l++) {
    for (let j = 1; j < P; j++) {
      const b = points[j] as number;
      for (let i = 0; i < j; i++) {
        const prev = (dp[l - 1] as number[])[i] as number;
        if (prev === INF) continue;
        const a = points[i] as number;
        const t = trimRange(clusters, a, b);
        if (t.to <= t.from) continue; // an empty line is never a plan
        let cost = prev + (t.cells - targetCells) ** 2;
        if (t.cells > maxCells) cost += (t.cells - maxCells) * OVERFLOW_PENALTY;
        if (i > 0) {
          const rank = rankAt.get(a) ?? -1;
          cost += rank < 0 ? NO_CLAUSE_PENALTY : rank * CLAUSE_RANK_STEP;
        }
        if (cost < ((dp[l] as number[])[j] as number)) {
          (dp[l] as number[])[j] = cost;
          (back[l] as number[])[j] = i;
        }
      }
    }
  }

  let bestLines = -1;
  let bestCost = INF;
  for (let l = 1; l <= lines; l++) {
    const c = (dp[l] as number[])[P - 1] as number;
    // Fewer lines wins ties: "one line unless it exceeds the character limit".
    if (c < bestCost - 1e-9) {
      bestCost = c;
      bestLines = l;
    }
  }
  if (bestLines < 0) return [[0, n]];

  const plan: [number, number][] = [];
  let j = P - 1;
  for (let l = bestLines; l >= 1; l--) {
    const i = (back[l] as number[])[j] as number;
    if (i < 0) break;
    plan.unshift([points[i] as number, points[j] as number]);
    j = i;
  }
  if (plan.length === 0) return [[0, n]];

  return applyShape(clusters, candidates, plan, maxCells, targetCells, shape);
}

/**
 * The pyramid tiebreak, applied only to the two-line case — which is the case every guide
 * actually describes, and the only one where "which line is shorter" has a single answer.
 *
 * A swap is accepted only when it costs no more raggedness than the incumbent (within the
 * shape weight), so shape never overrides balance; it only decides between equals.
 */
function applyShape(
  clusters: readonly Cluster[],
  candidates: readonly Candidate[],
  plan: readonly (readonly [number, number])[],
  maxCells: number,
  targetCells: number,
  shape: LineShape,
): readonly (readonly [number, number])[] {
  if (plan.length !== 2) return plan;
  const rankAt = new Map<number, number>();
  for (const c of candidates) rankAt.set(c.at, c.rank);

  const score = (at: number): number => {
    const first = trimRange(clusters, plan[0]?.[0] ?? 0, at);
    const second = trimRange(clusters, at, plan[1]?.[1] ?? clusters.length);
    if (first.to <= first.from || second.to <= second.from) return Number.POSITIVE_INFINITY;
    let cost = (first.cells - targetCells) ** 2 + (second.cells - targetCells) ** 2;
    if (first.cells > maxCells) cost += (first.cells - maxCells) * OVERFLOW_PENALTY;
    if (second.cells > maxCells) cost += (second.cells - maxCells) * OVERFLOW_PENALTY;
    const rank = rankAt.get(at) ?? -1;
    cost += rank < 0 ? NO_CLAUSE_PENALTY : rank * CLAUSE_RANK_STEP;
    const lean = shape === 'bottom-heavy' ? first.cells - second.cells : second.cells - first.cells;
    cost += Math.max(0, lean) * SHAPE_WEIGHT;
    return cost;
  };

  let best = plan[1]?.[0] ?? 0;
  let bestCost = score(best);
  for (const c of candidates) {
    const s = score(c.at);
    if (s < bestCost - 1e-9) {
      bestCost = s;
      best = c.at;
    }
  }
  return [
    [plan[0]?.[0] ?? 0, best],
    [best, plan[1]?.[1] ?? clusters.length],
  ];
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Convenience
───────────────────────────────────────────────────────────────────────────────────────*/

/** The broken text as strings — what `resegment` wants when it is building a cue's lines. */
export function breakText(
  text: string,
  opts: LineBreakOptions,
  measure: Measure,
  extras: BreakExtras = {},
): readonly string[] {
  return breakOffsets(text, opts, measure, extras).lines.map((l) => l.text);
}

/** Cell width of a string under the injected `Measure`. Never `string.length`. */
export function measureCells(text: string, measure: Measure): number {
  return measure.measure(text);
}
